import {
  BoxIcon,
  MoreHorizontalIcon,
  PencilIcon,
  StarIcon,
  TrashIcon,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ExecutionImageView } from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';
import {
  LIST_ROW,
  LIST_ROW_NAME,
  LIST_ROW_SECONDARY,
  LIST_ROWS,
} from '@/shared/components/listRow';

import { isExecutionImageError } from '../errors';
import { useSetDefaultExecutionImage } from '../hooks/useSetDefaultExecutionImage';
import { useCanWork } from '@/features/workspaces/canWork';

/**
 * Every image in the workspace's execution-image catalog, one row each
 * (FR-002): label, image reference, and a default marker, on the row recipe
 * People's members use (spec 228): icon leading, name over a monospace
 * reference, the row menu trailing, rows divided rather than boxed.
 *
 * "Default" is said once, by the badge (spec 129 FR-004): the default row
 * also carried a disabled "Default" button, and every other row a "Make
 * default" button, so the word appeared on every row. Making a row the
 * default is now a verb in its menu.
 */
export function ExecutionImageList({
  workspaceId,
  images,
  onEdit,
  onDelete,
}: {
  workspaceId: string;
  images: ExecutionImageView[];
  onEdit: (image: ExecutionImageView) => void;
  onDelete: (image: ExecutionImageView) => void;
}) {
  // No empty branch here. Whether there is anything to list is the page's
  // question, and it answers it with the same panel every other page uses —
  // icon, sentence and a way on. A list that also answered it gave a bordered
  // sentence with neither.

  return (
    <ul aria-label="Execution images" className={LIST_ROWS}>
      {images.map((image) => (
        <ExecutionImageRow
          key={image.id}
          workspaceId={workspaceId}
          image={image}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </ul>
  );
}

function ExecutionImageRow({
  workspaceId,
  image,
  onEdit,
  onDelete,
}: {
  workspaceId: string;
  image: ExecutionImageView;
  onEdit: (image: ExecutionImageView) => void;
  onDelete: (image: ExecutionImageView) => void;
}) {
  // Per row, as the button it replaces had it: local feedback, not state the
  // list coordinates across rows.
  const setDefault = useSetDefaultExecutionImage(workspaceId);
  const canWork = useCanWork();

  function makeDefault() {
    setDefault.mutate(image.id, {
      onSuccess: () => {
        toast.success(`${image.label} is now the default image.`);
      },
      onError: (error) => {
        const message = isExecutionImageError(error)
          ? error.message
          : `${image.label} could not be made default.`;
        toast.error(message);
      },
    });
  }

  return (
    <li className={LIST_ROW}>
      <BoxIcon
        aria-hidden="true"
        className="text-muted-foreground size-5 shrink-0"
        strokeWidth={1.5}
      />

      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-2">
          <span className={LIST_ROW_NAME}>{image.label}</span>
          {image.is_default ? (
            <Badge variant="secondary" className="shrink-0">
              Default
            </Badge>
          ) : null}
          {setDefault.isPending ? (
            <span className="text-muted-foreground shrink-0 text-xs">
              Making default…
            </span>
          ) : null}
        </span>
        <span className={cn(LIST_ROW_SECONDARY, 'font-mono')}>
          {image.image_reference}
        </span>
      </span>

      {canWork ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button variant="ghost" size="icon-sm" className="shrink-0">
                <MoreHorizontalIcon aria-hidden="true" />
                <span className="sr-only">Actions for {image.label}</span>
              </Button>
            }
          />
          <DropdownMenuContent align="end">
            {image.is_default ? null : (
              <DropdownMenuItem
                disabled={setDefault.isPending}
                onClick={makeDefault}
              >
                <StarIcon aria-hidden="true" />
                Make default
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => {
                onEdit(image);
              }}
            >
              <PencilIcon aria-hidden="true" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              variant="destructive"
              onClick={() => {
                onDelete(image);
              }}
            >
              <TrashIcon aria-hidden="true" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </li>
  );
}
